import { PANEL_OFF_COLOR, PANEL_OVERLAP_THRESHOLD, PANEL_ON_COLOR, DEV_STROKE_FACET_COLOR, FACET_HIGHLIGHT_OFF_COLOR } from '../../../constants';
import { Line } from 'react-konva';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { getPanelPointsFromConfig, getSnappedPosition, getRotatedLinePoints, checkIntersection, isOverLap } from '../PanelUtil';
import { useRefDispatch, useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { setNewlyAddedPanel } from 'store/slices/PanelSlice';
import { setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';
import { getAbsolutePoints } from 'components/DisplayEnergy/util';
import { ORIENTATION } from 'store/slices/AddPanelModalSlice/types';
import { KonvaEventObject } from 'konva/lib/Node';
import { KonvaGroupNames } from '../utils';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { getPanelPxScale } from '../../../util';

const CustomPanel = () => {
	const scale = useKonvaImageScale();
	const { panel, allRoofSegs, groundMetersPerPixel } = useSelector((state: RootState) => state.roofData.data);
	const { orientation: panelOrientation } = useSelector((state: RootState) => state.addPanelModal.data);
	const { moveStage, rotateStage, hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { enableMultipleAddPanel } = useSelector((state: RootState) => state.panelSlice.data);

	const { konvaRef } = useRefs();
	const movablePanelRef = useRef<Konva.Line | null>(null);
	const intersectedRef = useRef<Konva.Line | null>(null);
	const isDraggingRef = useRef(false);
	const wasHiddenRef = useRef(hideSatelliteOverlay);
	const refDispatch = useRefDispatch();
	const dispatch = useDispatch();
	const DRAG_FUNCTIONALITY = 'addPanel';

	const pxScale = getPanelPxScale(groundMetersPerPixel);

	useEffect(() => {
		refDispatch({ type: 'ADD_PANEL_REF', payload: { ref: movablePanelRef } });
	}, [refDispatch]);

	const panelPoints = useMemo(() => {
		let orientation: RasterRoofSegment['orientation'];
		if (panelOrientation === ORIENTATION.DEFAULT) {
			orientation = 'LANDSCAPE';
		} else if (panelOrientation === ORIENTATION.LANDSCAPE) {
			orientation = 'LANDSCAPE';
		} else {
			orientation = 'POTRAITT';
		}
		const width = panel.width;
		const height = panel.height;
		if (!konvaRef?.current) return [];
		return getPanelPointsFromConfig({ orientation, width, height }, scale);
	}, [panelOrientation, panel, konvaRef, scale]);

	const updatePanelPositionAndRotation = useCallback((e: KonvaEventObject<DragEvent>) => {
		if (hideSatelliteOverlay) return;
		if (!konvaRef?.current || !movablePanelRef?.current) return;
		intersectedRef.current = null;

		const targetFacets = konvaRef.current?.find(`.${KonvaGroupNames.existingFacet}`) as Konva.Line[];
		const intersectedFacet = checkIntersection(targetFacets, movablePanelRef?.current as Konva.Line);

		if (intersectedFacet) {
			intersectedRef.current = intersectedFacet;
			const matchingRoofSeg = allRoofSegs.find(seg => seg.id === intersectedFacet.id());
			if (matchingRoofSeg) {
				const parentGroup = intersectedFacet.getParent();
				const panelGroup = parentGroup?.findOne(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group | null;
				const panels = (panelGroup?.children || []) as Konva.Line[];
				
				let panelWidth = (matchingRoofSeg.panelWidth);
				let panelHeight = (matchingRoofSeg.panelHeight);
				if (panelOrientation === ORIENTATION.LANDSCAPE && panelHeight > panelWidth) {
					[panelWidth, panelHeight] = [panelHeight, panelWidth];
				}
				if (panelOrientation === ORIENTATION.PORTRAIT && panelWidth > panelHeight) {
					[panelWidth, panelHeight] = [panelHeight, panelWidth];
				}
				const rotationAngle = (matchingRoofSeg.azimuthDegrees) % 360;

				const chnageOrientation = (matchingRoofSeg.orientation === 'POTRAITT' && panelOrientation === ORIENTATION.LANDSCAPE) ||
				(matchingRoofSeg.orientation === 'LANDSCAPE' && panelOrientation === ORIENTATION.PORTRAIT);

				if (chnageOrientation) {
					// console.log('Blue Panel W/H:', panelWidth, panelHeight);
				}

				const dims = {
					width: (panelWidth/pxScale) * scale.x,
					height: (panelHeight/pxScale) * Math.cos(matchingRoofSeg.pitchDegrees * (Math.PI / 180)) * scale.y
				};
				const relativePosition = e.target.getRelativePointerPosition();

				if (!movablePanelRef?.current || !relativePosition) return;

				const points = getRotatedLinePoints(relativePosition, dims.width, dims.height, rotationAngle);
				movablePanelRef?.current.points(points);
				const { hasOverlap } = isOverLap(movablePanelRef.current, panels, PANEL_OVERLAP_THRESHOLD);
				const strokeColor = hasOverlap? 'red' : DEV_STROKE_FACET_COLOR;
				movablePanelRef.current.attrs.stroke = strokeColor;
				
				const closestEdge = getSnappedPosition(movablePanelRef.current.position(), movablePanelRef?.current, panels);

				if (closestEdge) movablePanelRef?.current.position(closestEdge);
			}
		} else {
			movablePanelRef.current.points(panelPoints);
			movablePanelRef.current.stroke(PANEL_ON_COLOR);
		}
	}, [konvaRef, allRoofSegs, scale, panelOrientation, panelPoints, movablePanelRef, hideSatelliteOverlay]);

	// When map only turns on mid-drag, freeze this panel preview: snapshot its exact
	// position into the global store (updatePanelPositionAndRotation above early-returns
	// while hidden, so geometry/position simply stop updating). On exit, restore the
	// snapshotted value — it sits at its last frozen position until the user moves the
	// mouse, at which point it follows again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDraggingRef.current || activeDragFunctionality !== DRAG_FUNCTIONALITY) return;
		const shape = movablePanelRef.current;
		if (!shape) return;

		if (hideSatelliteOverlay && !wasHidden) {
			dispatch(setMapOnlyFrozenPoint(shape.getAbsolutePosition()));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) shape.setAbsolutePosition(mapOnlyFrozenPoint);
			dispatch(setMapOnlyFrozenPoint(null));
			shape.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint, dispatch]);

	function insrtToIntersectedFacet() {
		isDraggingRef.current = false;
		dispatch(setActiveDragFunctionality(null));
		dispatch(setMapOnlyFrozenPoint(null));
		//check for overlap if red means do not allow to place
		if(movablePanelRef?.current?.attrs.stroke === 'red') return;

		const intersectedFacet = intersectedRef.current;
		const panelToInsert = movablePanelRef?.current;
		if (!intersectedFacet || !panelToInsert || !konvaRef?.current) return;

		const matchingRoofSeg = allRoofSegs.find(seg => seg.id === intersectedFacet.id());
		if (!matchingRoofSeg) return;
		const exteriorCoords: number[][] = [];
		const coords = getAbsolutePoints(panelToInsert, scale, konvaRef?.current);
		for (let i = 0; i < coords.length; i += 2) {
			exteriorCoords.push([coords[i], coords[i + 1]]);
		}
		const shouldClosePolygon = exteriorCoords.length === 4; // since panels are rect;
		if (shouldClosePolygon) exteriorCoords.push(exteriorCoords[0]);

		panelToInsert.visible(false);
		intersectedRef.current = null;
		let newOrientation = panelOrientation === ORIENTATION.DEFAULT ? matchingRoofSeg.orientation : panelOrientation;
		newOrientation = newOrientation === 'POTRAITT' ? ORIENTATION.PORTRAIT : newOrientation;
		
		dispatch(setNewlyAddedPanel({
			addedPanels: [{
				exteriorCoords,
				orientation: newOrientation,
				roofSegmentId: matchingRoofSeg.id,
			}]
		}));
		intersectedRef.current = null;
	}

	useEffect(() => {
		if(enableMultipleAddPanel && konvaRef?.current) {
			const targetFacets = konvaRef.current?.find(`.${KonvaGroupNames.existingFacet}`) as Konva.Line[];
			targetFacets.forEach(eachShape => {
				eachShape.fill(FACET_HIGHLIGHT_OFF_COLOR);
			});
			konvaRef.current.batchDraw();
		}
	}, [enableMultipleAddPanel]);

	useEffect(() => {
		if (movablePanelRef.current) {
			movablePanelRef.current.moveToTop();
			movablePanelRef.current.getLayer()?.batchDraw();
		}
	}, [panelPoints]); 

	return (
		<Line
			visible={false}
			ref={movablePanelRef}
			name="movable_panel"
			fill={PANEL_OFF_COLOR}
			listening={true}
			strokeWidth={1.4}
			stroke={'black'}
			points={panelPoints}
			draggable={!moveStage && !rotateStage}
			closed={true}
			onDragStart={()=> {
				intersectedRef.current = null;
				movablePanelRef.current?.stroke(DEV_STROKE_FACET_COLOR);
				isDraggingRef.current = true;
				dispatch(setActiveDragFunctionality(DRAG_FUNCTIONALITY));
			}}
			strokeScaleEnabled={false}
			onDragMove={(e) => updatePanelPositionAndRotation(e)}
			onDragEnd={() => insrtToIntersectedFacet()}
		/>
	);
};

export default memo(CustomPanel);